import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { isApiError } from '../api/errors'
import { QueryError } from '../components/QueryError'
import { StatusMessage } from '../components/StatusMessage'
import { useCreativeQuery, useRecordQuery, useStatesQuery } from '../hooks/useDisclosureQueries'
import {
  adTypeLabel,
  formatAmount,
  formatDateTime,
  formatImpressions,
  isoDateTime,
  positionLabel,
  subjectNameLabel,
  subjectTypeLabel,
  textOrFallback,
} from '../lib/format'
import type { Creative, DisclosureRecord } from '../types'

function DefinitionRow({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="definition">
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/** ad_type is an open set, so unknown values fall through to a plain link. */
const IMAGE_AD_TYPES = new Set(['banner', 'native'])
const VIDEO_AD_TYPES = new Set(['video'])
const AUDIO_AD_TYPES = new Set(['audio'])

export function CreativeMedia({ creative, purchaser }: { creative: Creative; purchaser: string }) {
  const [failed, setFailed] = useState(false)
  const adType = creative.ad_type?.toLowerCase() ?? ''

  // Never leave a broken image or an empty frame on a legal record. The reason for
  // the failure is not knowable here, so it is not guessed at — only the documented
  // fact that the link is temporary, which makes reloading a useful action.
  if (failed) {
    return (
      <StatusMessage tone="empty" title="The archived copy could not be loaded">
        <p>The link to it is temporary. Reload the page to request a new one.</p>
      </StatusMessage>
    )
  }

  if (VIDEO_AD_TYPES.has(adType)) {
    return (
      <video className="creative-media" controls preload="metadata" onError={() => setFailed(true)}>
        <source src={creative.url} />
        Your browser cannot play this archived advertisement.{' '}
        <a href={creative.url}>Open the archived file</a>.
      </video>
    )
  }

  if (AUDIO_AD_TYPES.has(adType)) {
    return (
      <audio className="creative-audio" controls preload="metadata" onError={() => setFailed(true)}>
        <source src={creative.url} />
        Your browser cannot play this archived advertisement.{' '}
        <a href={creative.url}>Open the archived file</a>.
      </audio>
    )
  }

  if (IMAGE_AD_TYPES.has(adType)) {
    return (
      <img
        className="creative-media"
        src={creative.url}
        alt={`Archived ${adTypeLabel(creative.ad_type).toLowerCase()} advertisement placed by ${purchaser}`}
        onError={() => setFailed(true)}
      />
    )
  }

  // An ad_type this build does not know how to embed. The doc calls these open
  // sets, so a new one must still be reachable rather than silently dropped.
  return (
    <p>
      <a href={creative.url} rel="noreferrer">
        {adType
          ? `Open the archived advertisement (${adTypeLabel(creative.ad_type)})`
          : 'Open the archived advertisement'}
      </a>
    </p>
  )
}

/**
 * Wording is taken verbatim from docs/api.md §7, which prescribes it: "word the page
 * as 'no copy of this advertisement is currently available'".
 *
 * It deliberately says nothing about WHY no copy exists. The API does not
 * distinguish a third-party-served ad (permanent) from archiving that has not
 * completed yet (temporary), so any explanation here would be a guess presented as
 * fact on a legal record. Do not add one.
 */
function NoCopyAvailable() {
  return (
    <StatusMessage tone="empty" title="No copy of this advertisement is currently available" />
  )
}

function CreativeSection({ record }: { record: DisclosureRecord }) {
  // Requested only once this page is open, and only when the record says a copy
  // exists — never prefetched from the results list.
  const creativeQuery = useCreativeQuery(String(record.id), record.creative_available)

  if (!record.creative_available) {
    return <NoCopyAvailable />
  }

  if (creativeQuery.isPending) {
    return <StatusMessage title="Loading the archived copy…" />
  }

  if (creativeQuery.isError) {
    // "no archived copy" is a normal outcome, not a fault — and is deliberately
    // worded differently from "disclosure not found" so the two can be told apart.
    if (isApiError(creativeQuery.error) && creativeQuery.error.kind === 'creative_unavailable') {
      return <NoCopyAvailable />
    }
    return (
      <QueryError
        error={creativeQuery.error}
        onRetry={() => void creativeQuery.refetch()}
        retrying={creativeQuery.isFetching}
      />
    )
  }

  return (
    <div className="stack">
      <CreativeMedia creative={creativeQuery.data} purchaser={record.purchaser_name} />
      <p className="field-hint">
        The archived copy is served over a temporary link that expires after about{' '}
        {Math.max(1, Math.round(creativeQuery.data.expires_in / 60))} minutes. Reload this page to
        request a new one.
      </p>
    </div>
  )
}

export function RecordDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [urlParams] = useSearchParams()

  const returnQuery = urlParams.get('from') ?? ''
  const backHref = returnQuery ? `/search?${returnQuery}` : '/search'

  /**
   * The states the user searched, carried over so this page shows the same states
   * the results row did.
   *
   * They are intersected with /states first: if none of the codes sent are states
   * the archive publishes, the API discards the filter and returns EVERY state
   * rather than none — silently showing a wider set than was asked for. Waiting for
   * /states (normally already cached from the search page) is the price of not
   * risking that.
   */
  const statesQuery = useStatesQuery()
  const publishedCodes = new Set((statesQuery.data ?? []).map((state) => state.iso_code))
  const requestedStates = (new URLSearchParams(returnQuery).get('states') ?? '')
    .split(',')
    .map((code) => code.trim().toUpperCase())
    .filter((code) => publishedCodes.has(code))

  const statesSettled = statesQuery.isSuccess || statesQuery.isError
  const recordQuery = useRecordQuery(id, requestedStates, statesSettled)

  const backLink = (
    <p>
      <Link to={backHref} className="back-link" viewTransition>
        ← Back to search
      </Link>
    </p>
  )

  if (recordQuery.isPending) {
    return (
      <div className="stack-lg">
        {backLink}
        <StatusMessage title="Loading disclosure record…" />
      </div>
    )
  }

  if (recordQuery.isError) {
    return (
      <div className="stack-lg">
        {backLink}
        <QueryError
          error={recordQuery.error}
          onRetry={() => void recordQuery.refetch()}
          retrying={recordQuery.isFetching}
        />
      </div>
    )
  }

  const record = recordQuery.data
  const narrowedByStates = requestedStates.length > 0

  return (
    <article className="stack-lg">
      {backLink}

      <header className="page-header">
        <p className="eyebrow">Disclosure record #{record.id}</p>
        <h1>{record.purchaser_name}</h1>
        <p className="lede">
          Published{' '}
          <time dateTime={isoDateTime(record.published_at)}>
            {formatDateTime(record.published_at)}
          </time>
          . This is the version of the disclosure currently on file. If its details are corrected,
          this record is updated to match and no earlier version is kept.
        </p>
      </header>

      <section className="panel" aria-labelledby="purchaser-heading">
        <h2 id="purchaser-heading">Purchaser</h2>
        {/* Every field below uses omitempty: an absent key is normal, not an error. */}
        <dl className="definition-grid">
          <DefinitionRow term="Purchaser">{record.purchaser_name}</DefinitionRow>
          <DefinitionRow term="Committee ID">{textOrFallback(record.committee_id)}</DefinitionRow>
          <DefinitionRow term="Treasurer">{textOrFallback(record.treasurer_name)}</DefinitionRow>
          <DefinitionRow term="Contact name">{textOrFallback(record.contact_name)}</DefinitionRow>
          <DefinitionRow term="Contact details">{textOrFallback(record.contact_info)}</DefinitionRow>
          {/* Pre-formatted single line from the API — rendered verbatim, never re-parsed. */}
          <DefinitionRow term="Address">{textOrFallback(record.address)}</DefinitionRow>
        </dl>
      </section>

      <section className="panel" aria-labelledby="ad-heading">
        <h2 id="ad-heading">Advertisement</h2>
        <dl className="definition-grid">
          {/* subject_type decides how subject_name is labelled: a person or a measure. */}
          <DefinitionRow term={subjectNameLabel(record.subject_type)}>
            {record.subject_name}
          </DefinitionRow>
          <DefinitionRow term="Subject type">{subjectTypeLabel(record.subject_type)}</DefinitionRow>
          <DefinitionRow term="Position">{positionLabel(record.position)}</DefinitionRow>
          <DefinitionRow term="Ad type">{adTypeLabel(record.ad_type)}</DefinitionRow>
          <DefinitionRow term={narrowedByStates ? 'States shown' : 'States published for'}>
            {record.states.length > 0 ? record.states.join(', ') : 'None reported'}
          </DefinitionRow>
          <DefinitionRow term="Audience targeting">
            {textOrFallback(record.audience_summary)}
          </DefinitionRow>
        </dl>
        {narrowedByStates ? (
          <p className="field-hint">
            Narrowed to the states you searched. <Link to={`/records/${record.id}`}>View every
            state this record was published for</Link>.
          </p>
        ) : null}
      </section>

      {/* Figures are per state. They are never summed: each state regulates its own
          delivery numbers and a combined total is not a figure any regulator asked for. */}
      <section className="panel" aria-labelledby="figures-heading">
        <h2 id="figures-heading">Delivery figures by state</h2>
        <p className="panel-intro">
          Each state’s figures are reported separately, as each state regulates its own delivery.
          These figures are not combined into a total.
        </p>

        {record.figures.length === 0 ? (
          /* Doc §7: handle an empty figures array as "no figures reported for the
             selected states", not as a broken record. No cause is offered — the
             response does not carry one. */
          <StatusMessage
            tone="empty"
            title={
              narrowedByStates
                ? 'No figures reported for the selected states'
                : 'No figures reported for this record'
            }
          />
        ) : (
          <div className="table-wrap">
            <table>
              <caption className="visually-hidden">
                Impressions and amount paid, reported separately for each state
              </caption>
              <thead>
                <tr>
                  <th scope="col">State</th>
                  <th scope="col">Impressions</th>
                  <th scope="col">Amount paid</th>
                </tr>
              </thead>
              <tbody>
                {record.figures.map((figure) => (
                  <tr key={figure.state}>
                    <th scope="row">{figure.state}</th>
                    {/* null means not yet computed — never rendered as 0. */}
                    <td className={figure.impressions === null ? 'unreported' : undefined}>
                      {formatImpressions(figure.impressions)}
                    </td>
                    <td className={figure.amount_paid === null ? 'unreported' : undefined}>
                      {formatAmount(figure.amount_paid)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="table-note">
          “Not yet reported” means the figure has not been computed for this record yet. It does not
          mean zero.
        </p>
      </section>

      <section className="panel" aria-labelledby="dates-heading">
        <h2 id="dates-heading">Dates</h2>
        <div className="date-columns">
          <div>
            <h3>When the advertisement served</h3>
            <dl className="definition-grid">
              <DefinitionRow term="First impression">
                <time dateTime={isoDateTime(record.first_impression_at)}>
                  {formatDateTime(record.first_impression_at)}
                </time>
              </DefinitionRow>
              <DefinitionRow term="Last impression">
                <time dateTime={isoDateTime(record.last_impression_at)}>
                  {formatDateTime(record.last_impression_at)}
                </time>
              </DefinitionRow>
            </dl>
          </div>
          <div>
            <h3>When it was booked to run</h3>
            <dl className="definition-grid">
              <DefinitionRow term="Scheduled start">
                <time dateTime={isoDateTime(record.scheduled_start_at)}>
                  {formatDateTime(record.scheduled_start_at)}
                </time>
              </DefinitionRow>
              <DefinitionRow term="Scheduled end">
                <time dateTime={isoDateTime(record.scheduled_end_at)}>
                  {formatDateTime(record.scheduled_end_at)}
                </time>
              </DefinitionRow>
            </dl>
          </div>
        </div>
        <p className="table-note">
          Served dates and booked dates are reported separately and are not interchangeable. Record
          last updated{' '}
          <time dateTime={isoDateTime(record.updated_at)}>{formatDateTime(record.updated_at)}</time>.
        </p>
      </section>

      <section className="panel" aria-labelledby="creative-heading">
        <h2 id="creative-heading">Archived advertisement</h2>
        <CreativeSection record={record} />
      </section>
    </article>
  )
}
